import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';
import EntityCompliancePanel from '../components/EntityCompliancePanel';

export default function CompanyProfilePage() {
  const { can } = useAuth();
  const [form, setForm] = useState({
    name: '', legal_name: '', dba: '', ein: '',
    phone: '', email: '', website: '',
    address: '', city: '', state: 'TX', zip: '',
    mc_number: '', dot_number: '', tdlr_license: '', notes: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [companyId, setCompanyId] = useState(null);

  const loadCompany = useCallback(async () => {
    setLoading(true);
    try {
      const companies = await api.getCompanies();
      const company = Array.isArray(companies) && companies.length > 0 ? companies[0] : null;
      if (company) {
        setCompanyId(company.id);
        setForm({
          name: company.name || '',
          legal_name: company.legal_name || '',
          dba: company.dba || '',
          ein: company.ein || '',
          phone: company.phone || '',
          email: company.email || '',
          website: company.website || '',
          address: company.address || '',
          city: company.city || '',
          state: company.state || 'TX',
          zip: company.zip || '',
          mc_number: company.mc_number || '',
          dot_number: company.dot_number || '',
          tdlr_license: company.tdlr_license || '',
          notes: company.notes || '',
        });
      }
    } catch (err) {
      setError(err.message || 'Failed to load company');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadCompany(); }, [loadCompany]);

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      if (companyId) {
        await api.updateCompany(companyId, form);
      } else {
        const created = await api.createCompany(form);
        setCompanyId(created.id);
      }
      setSuccess('Company profile saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save company');
    }
    setSaving(false);
  }

  function handleChange(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  }

  if (!can('manage_admin')) {
    return <div className="page-content"><p>You do not have permission to access this page.</p></div>;
  }

  if (loading) {
    return <div className="page-content"><div className="loading-spinner">Loading company profile...</div></div>;
  }

  return (
    <div className="admin-page">
      <div className="maint-types__header">
        <h1>Company Profile</h1>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <form onSubmit={handleSave}>
        <div style={{ background: 'white', borderRadius: '14px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.03)', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b', marginBottom: '16px' }}>General Information</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Company Name *</label>
              <input name="name" value={form.name} onChange={handleChange} required className="form-input" />
            </div>
            <div className="form-group">
              <label>Legal Name</label>
              <input name="legal_name" value={form.legal_name} onChange={handleChange} className="form-input" />
            </div>
            <div className="form-group">
              <label>DBA (Doing Business As)</label>
              <input name="dba" value={form.dba} onChange={handleChange} className="form-input" />
            </div>
            <div className="form-group">
              <label>EIN</label>
              <input name="ein" value={form.ein} onChange={handleChange} className="form-input" placeholder="XX-XXXXXXX" />
            </div>
          </div>
        </div>

        <div style={{ background: 'white', borderRadius: '14px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.03)', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b', marginBottom: '16px' }}>Contact Information</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Phone</label>
              <input name="phone" value={form.phone} onChange={handleChange} className="form-input" />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input name="email" type="email" value={form.email} onChange={handleChange} className="form-input" />
            </div>
            <div className="form-group">
              <label>Website</label>
              <input name="website" value={form.website} onChange={handleChange} className="form-input" placeholder="https://" />
            </div>
          </div>
        </div>

        <div style={{ background: 'white', borderRadius: '14px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.03)', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b', marginBottom: '16px' }}>Address</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Address</label>
              <input name="address" value={form.address} onChange={handleChange} className="form-input" />
            </div>
            <div className="form-group">
              <label>City</label>
              <input name="city" value={form.city} onChange={handleChange} className="form-input" />
            </div>
            <div className="form-group">
              <label>State</label>
              <input name="state" value={form.state} onChange={handleChange} className="form-input" />
            </div>
            <div className="form-group">
              <label>Zip</label>
              <input name="zip" value={form.zip} onChange={handleChange} className="form-input" />
            </div>
          </div>
        </div>

        <div style={{ background: 'white', borderRadius: '14px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.03)', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b', marginBottom: '16px' }}>Regulatory & Licensing</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>MC Number</label>
              <input name="mc_number" value={form.mc_number} onChange={handleChange} className="form-input" />
            </div>
            <div className="form-group">
              <label>DOT Number</label>
              <input name="dot_number" value={form.dot_number} onChange={handleChange} className="form-input" />
            </div>
            <div className="form-group">
              <label>TDLR License</label>
              <input name="tdlr_license" value={form.tdlr_license} onChange={handleChange} className="form-input" />
            </div>
          </div>
        </div>

        <div style={{ background: 'white', borderRadius: '14px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.03)', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b', marginBottom: '16px' }}>Notes</h3>
          <div className="form-group">
            <textarea name="notes" value={form.notes} onChange={handleChange} className="form-input" rows={4} />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button type="submit" className="btn btn-gold" disabled={saving}>
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
        </div>
      </form>

      {companyId && (
        <EntityCompliancePanel
          entityType="company"
          entityId={companyId}
          entityName={form.name}
        />
      )}
    </div>
  );
}
